import React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  X,
  ExternalLink,
  Clock,
  Check,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Notification, NotificationSeverity } from '../../hooks/useNotifications';
import { formatEasternDate } from '@/utils/dateFormatter';

interface NotificationItemProps {
  /** Notification data */
  notification: Notification;
  /**
   * Take ownership of this alert — a TRUE acknowledgment (persists is_acknowledged /
   * acknowledged_by / acknowledged_at). This is deliberate: it is NOT triggered by merely
   * clicking/reading the row, only by the explicit Acknowledge button.
   */
  onAcknowledge?: (id: string) => void;
  /** Callback when notification is removed */
  onRemove?: (id: string) => void;
  /** Callback when notification is clicked (navigates to the source; does NOT acknowledge) */
  onClick?: (notification: Notification) => void;
  /** Whether to show the remove button */
  showRemoveButton?: boolean;
  /** Custom CSS classes */
  className?: string;
}

/**
 * NotificationItem component for displaying individual notifications
 * 
 * Features:
 * - Severity icons and colors
 * - Click to navigate to resource
 * - Mark as read/unread
 * - Remove notification
 * - Timestamp display
 * - Unread indicator
 * - Keyboard navigation support
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onAcknowledge,
  onRemove,
  onClick,
  showRemoveButton = true,
  className = '',
}) => {
  const navigate = useNavigate();

  /**
   * Get severity icon component
   */
  const getSeverityIcon = (severity: NotificationSeverity) => {
    const iconProps = { className: 'w-4 h-4 flex-shrink-0' };
    
    switch (severity) {
      case 'error':
        return <AlertCircle {...iconProps} className={`${iconProps.className} text-red-500`} />;
      case 'warning':
        return <AlertTriangle {...iconProps} className={`${iconProps.className} text-orange-500`} />;
      case 'success':
        return <CheckCircle {...iconProps} className={`${iconProps.className} text-green-500`} />;
      case 'info':
      default:
        return <Info {...iconProps} className={`${iconProps.className} text-blue-500`} />;
    }
  };

  /**
   * Get severity-based styling
   */
  const getSeverityStyles = (severity: NotificationSeverity) => {
    switch (severity) {
      case 'error':
        return 'border-l-red-400 dark:border-l-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30';
      case 'warning':
        return 'border-l-orange-400 dark:border-l-orange-500 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30';
      case 'success':
        return 'border-l-green-400 dark:border-l-green-500 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30';
      case 'info':
      default:
        return 'border-l-blue-400 dark:border-l-blue-500 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30';
    }
  };

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return formatEasternDate(timestamp);
  };

  /**
   * Handle notification click — opens/navigates to the alert so it can be reviewed.
   * Reading is NOT acknowledging: clicking never takes ownership, so the unacknowledged
   * indicator + the "needs attention" count stay until a deliberate Acknowledge.
   */
  const handleClick = () => {
    if (onClick) {
      onClick(notification);
      return;
    }
    if (notification.resourceUrl) {
      navigate(notification.resourceUrl);
    }
  };

  /**
   * Take ownership — the deliberate, true acknowledgment (does not fire on a read-click).
   */
  const handleAcknowledge = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onAcknowledge) onAcknowledge(notification.id);
  };

  /**
   * Handle remove button click
   */
  const handleRemove = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (onRemove) {
      onRemove(notification.id);
    }
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      if (showRemoveButton && onRemove) {
        event.preventDefault();
        onRemove(notification.id);
      }
    }
  };

  /**
   * Get main container styling
   */
  const getContainerStyles = () => {
    const baseStyles = 'relative border-l-4 p-3 transition-all duration-200 cursor-pointer';
    const readStyles = notification.read
      ? 'bg-[var(--color-bg-subtle)] hover:opacity-80 opacity-75'
      : 'shadow-sm';
    const severityStyles = getSeverityStyles(notification.severity);
    
    return `${baseStyles} ${severityStyles} ${readStyles}`;
  };

  return (
    <div
      className={`${getContainerStyles()} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Notification: ${notification.title}. ${notification.read ? 'Read' : 'Unread'}`}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <div className="absolute top-2 right-2 w-2 h-2 bg-blue-600 rounded-full" />
      )}

      {/* Main content */}
      <div className="flex items-start space-x-3">
        {/* Severity icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getSeverityIcon(notification.severity)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title and timestamp row */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className={`text-sm font-medium truncate ${
              notification.read ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-primary)]'
            }`}>
              {notification.title}
            </h4>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Timestamp */}
              <div className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
                <Clock className="w-3 h-3" />
                <span>{formatTimestamp(notification.timestamp)}</span>
              </div>
            </div>
          </div>

          {/* Message */}
          <p className={`text-sm mb-2 ${
            notification.read ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-secondary)]'
          }`}>
            {notification.message}
          </p>

          {/* Spec 255: acknowledgement attribution — who acked + when (vital multi-user context).
              Shows the instant ownership is taken (optimistic); the "by <who>" fills in on reconcile. */}
          {notification.acknowledged && (
            <p className="flex items-center gap-1 text-xs text-green-700 dark:text-green-400 mb-2">
              <CheckCircle className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                Acknowledged{notification.acknowledgedByName ? ` by ${notification.acknowledgedByName}` : ''}
                {notification.acknowledgedAt ? ` · ${formatTimestamp(new Date(notification.acknowledgedAt))}` : ''}
              </span>
            </p>
          )}

          {/* Resource info and actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Resource info */}
              {notification.resourceType && notification.resourceId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] rounded-md">
                  <span className="capitalize">{notification.resourceType}</span>
                  <span>·</span>
                  <span className="font-mono">{notification.resourceId}</span>
                </span>
              )}

              {/* External link indicator */}
              {notification.resourceUrl && (
                <ExternalLink className="w-3 h-3 text-[var(--color-text-secondary)]" />
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              {/* Acknowledge — deliberate ownership (only appears while unacknowledged) */}
              {!notification.acknowledged && onAcknowledge && (
                <button
                  onClick={handleAcknowledge}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-green-700 transition-colors hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/25"
                  aria-label="Acknowledge — take ownership of this alert"
                  title="Acknowledge — take ownership of this alert"
                >
                  <Check className="h-3.5 w-3.5" />
                  Acknowledge
                </button>
              )}
              {/* Remove button */}
              {showRemoveButton && onRemove && (
                <button
                  onClick={handleRemove}
                  className="p-1 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] rounded transition-colors"
                  aria-label="Remove notification"
                  title="Remove notification"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hover effects */}
      <div className="absolute inset-0 border border-transparent hover:border-[var(--color-border)] rounded transition-colors pointer-events-none" />
    </div>
  );
};

export default NotificationItem;